import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import {
  IonSelect,
  IonSelectOption,
  IonToggle,
  useIonToast,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  isPlatform,
  IonFooter,
  IonAlert,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import { IconTrash, IconEdit, IconPlus } from "@tabler/icons-react";

import AiApi from "../../../../api/ai";
import SkeletonText from "../../../SkeletonText";
import { AiFunction } from "../../../../interfaces";

import { AiLocation, HelpRule, WorkingHours } from "./types";
import { Wrapper } from "../../../new-conversation";
import Alert from "../../../common/alert";

import styles from "./styles.module.css";
import globalOutboundStyles from "../../../outbound/styles.module.css";
import modalStyles from "../../../../theme/modal.module.css";
import inputStyles from "../../../../theme/input.module.css";
import { useAppStore } from "../../../../store/app";

const isMobile = isPlatform("android") || isPlatform("ios");

export default function Escalations() {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const [present] = useIonToast();

  const locations = useAppStore((state) => state.settings.locations) || [];

  const [stateLocations, setLocations] = useState<AiLocation[]>();
  const [currentRule, setCurrentRule] = useState<HelpRule>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);

  /*
  const {
    data: aiData,
    isLoading: isGettingAi,
    isRefetching: isRefetchingAi,
  } = useQuery({
    queryFn: () => AiApi.getFunctions(),
    queryKey: ["getAiFunctions"],
    refetchOnWindowFocus: false,
  });
  */

  const { data: helpRules } = useQuery({
    queryFn: () => AiApi.getHelpRules(),
    queryKey: ["getHelpRules"],
    refetchOnWindowFocus: false,
  });

  const {
    data: aiSettings,
    isLoading: isGettingAiSettings,
    isRefetching: isRefetchingAiSettings,
  } = useQuery({
    queryFn: () => AiApi.getAiSettings(),
    queryKey: ["getAiSettings"],
    refetchOnWindowFocus: false,
  });

  const showToast = (id: string, duration = 4000, cssClass = "success-toast") =>
    present({
      message: intl.formatMessage({ id }),
      duration,
      color: "light",
      position: "top",
      cssClass,
    });

  const mutateUpdateHelpRule = useMutation({
    mutationFn: AiApi.createUpdateHelpRule,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getHelpRules"] });
      setCurrentRule(undefined);
      setIsModalOpen(false);
      return showToast("aiUpdateSucces");
    },
    onError: () => {
      return showToast("common.tryItLater", 5000, "error-toast");
    },
  });

  const mutateUpdateFunction = useMutation({
    mutationFn: AiApi.toggleHelpRulesEnable,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["getAiSettings"] }),
      ]);
      return showToast("aiUpdateSucces");
    },
    onError: () => {
      return showToast("common.tryItLater", 5000, "error-toast");
    },
  });

  const mutateDeleteRule = useMutation({
    mutationFn: AiApi.deleteHelpRule,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getHelpRules"] });
      setCurrentRule(undefined);
      setOpenAlert(false);
      return showToast("aiUpdateSucces");
    },
    onError: () => {
      return showToast("common.tryItLater", 5000, "error-toast");
    },
  });

  const handleWorkingHoursChange = async (
    location: string,
    field: string,
    workingHours: WorkingHours,
  ) => {
    const { err } = await AiApi.putAiSettingsByField(
      field,
      workingHours,
      location,
    );

    if (err) {
      return showToast("common.tryItLater", 5000, "error-toast");
    }

    setLocations((prev = []) => {
      const idx = prev?.findIndex((l) => l.location === location);

      const newLocations =
        idx > -1
          ? prev.map((l, i) => (i === idx ? { location, workingHours } : l))
          : [...prev, { location, workingHours }];

      return newLocations;
    });
  };

  const onHideModal = () => {
    setIsModalOpen(false);
    setCurrentRule(undefined);
  };

  const onBeforeDismiss = (
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e?.preventDefault?.();

    setIsModalOpen(false);
    return setCurrentRule(undefined);
  };

  const onBeforeUpdateRule = (rule: HelpRule) => {
    setCurrentRule(rule);
    setIsModalOpen(true);
  };

  const onBeforeDeleteRule = (rule: HelpRule) => {
    setCurrentRule(rule);
    setOpenAlert(true);
  };

  const onBeforeSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (currentRule?.name && currentRule?.description) {
      return mutateUpdateHelpRule.mutate({
        ruleId: currentRule?._id,
        name: currentRule.name,
        description: currentRule.description,
        active: true,
      });
    }
  };

  const onEnableDisableRule = (ruleId: string, active: boolean) => {
    return mutateUpdateHelpRule.mutate({
      ruleId,
      active,
      name: undefined,
      description: undefined,
    });
  };

  const onDeleteRule = () => {
    if (currentRule?._id) {
      return mutateDeleteRule.mutate({
        ruleId: currentRule._id,
      });
    }
  };

  const onHideAlert = () => {
    setOpenAlert(false);
    setCurrentRule(undefined);
  };

  useEffect(() => {
    if (aiSettings?.data?.aiSettings?.locations?.length > 0) {
      setLocations(aiSettings.data.aiSettings.locations);
    }
  }, [aiSettings?.data?.aiSettings?.locations]);

  const isRequesting = isGettingAiSettings || isRefetchingAiSettings;

  console.log(aiSettings);
  const isFunctionActive = aiSettings?.data?.aiSettings?.escalationsActive;

  return isRequesting ? (
    <SkeletonText rows={12} />
  ) : (
    <div className="flex flex-column gap-4 pb-5">
      {/* Enable escalations (globally) */}
      <div className="visito-card">
        <div className="visito-card-body">
          <div className="flex align-items-center justify-content-between gap-2">
            <label className="mb-0" htmlFor="enable-help">
              <FormattedMessage
                id={
                  isFunctionActive
                    ? "ai.settings.escalations.enable"
                    : "ai.settings.escalations.disabled"
                }
              />
            </label>
            <IonToggle
              id="enable-help"
              name="enable-help"
              checked={isFunctionActive}
              onIonChange={(ev) =>
                // @ts-ignore
                mutateUpdateFunction.mutate({ active: ev.detail.checked })
              }
              mode="ios"
              className="ios visito-toggle"
            />
          </div>
          <p className="description mb-0 fs-2">
            <FormattedMessage id="ai.settings.escalations.enable.desc" />
          </p>
        </div>
      </div>

      {!isFunctionActive && (
        <Alert
          type="info"
          message={intl.formatMessage({
            id: "ai.settings.escalations.disabled.alert",
          })}
          className="p-2"
        />
      )}
      {/* Display help rules */}
      <div
        className={`flex flex-column gap-2 relative ${
          !isFunctionActive ? styles.blur : ""
        }`}
      >
        <div>
          <p className="mb-0 fs-4">
            <FormattedMessage id="ai.settings.escalations.setup" />
          </p>
          <p className="mb-0 description fs-3">
            <FormattedMessage id="ai.settings.escalations.setup.desc" />
          </p>
        </div>
        <div className="visito-card">
          <div className="visito-card-body">
            <div className="row">
              <div className="col-4">
                <p className="mb-0">
                  <FormattedMessage id="ai.settings.escalations.rules.name" />
                </p>
              </div>
              <div className="col-6">
                <p className="mb-0">
                  <FormattedMessage id="ai.settings.escalations.rules.content" />
                </p>
              </div>
              <div className="col-2 flex justify-content-end">
                <button
                  className="btn btn-visito-primary btn-sm flex align-items-center gap-1"
                  onClick={() => setIsModalOpen(true)}
                >
                  <IconPlus size={18} />
                  <FormattedMessage id="ai.settings.escalations.rules.add" />
                </button>
              </div>

              <div className={`col-12 ${styles.divider}`} />

              <div className="col-12">
                <p className="description mb-0">
                  <FormattedMessage id="ai.settings.escalations.rules.build" />
                </p>
              </div>

              <div className={`col-12 ${styles.divider}`} />

              {helpRules?.defaultRules?.map((rule: HelpRule) => (
                <React.Fragment key={rule._id}>
                  <div className="col-4 py-2 flex align-items-center gap-2">
                    <IonToggle
                      checked={rule.active}
                      onIonChange={(ev) =>
                        onEnableDisableRule(String(rule._id), ev.detail.checked)
                      }
                      mode="ios"
                      className="ios visito-toggle"
                    />
                    <span className="">{rule.name}</span>
                  </div>
                  <div className="col-6 py-2 description">
                    {rule.description}
                  </div>
                  <div className="col-2 py-2 flex justify-content-end align-items-center gap-2" />
                  <div className={`col-12 ${styles.divider}`} />
                </React.Fragment>
              ))}

              {helpRules?.customRules?.length > 0 && (
                <>
                  <div className="col-12">
                    <p className="description mb-0">
                      <FormattedMessage id="ai.settings.escalations.rules.custom" />
                    </p>
                  </div>

                  <div className={`col-12 ${styles.divider}`} />

                  {helpRules?.customRules.map(
                    (rule: HelpRule, index: number) => (
                      <React.Fragment key={rule._id}>
                        <div className="col-4 py-2 flex align-items-center gap-2">
                          <IonToggle
                            checked={rule.active}
                            onIonChange={(ev) =>
                              onEnableDisableRule(
                                String(rule._id),
                                ev.detail.checked,
                              )
                            }
                            mode="ios"
                            className="ios visito-toggle"
                          />
                          <span className="">{rule.name}</span>
                        </div>
                        <div className="col-6 py-2 description">
                          {rule.description}
                        </div>
                        <div className="col-2 py-2 flex justify-content-end align-items-center gap-2">
                          <IconEdit
                            size={23}
                            style={{ color: "var(--ion-color-step-750)" }}
                            role="button"
                            onClick={() => onBeforeUpdateRule(rule)}
                          />
                          <IconTrash
                            size={22}
                            style={{ color: "var(--visito-color-danger)" }}
                            role="button"
                            onClick={() => onBeforeDeleteRule(rule)}
                          />
                        </div>
                        {index !== helpRules?.customRules?.length - 1 && (
                          <div className={`col-12 ${styles.divider}`} />
                        )}
                      </React.Fragment>
                    ),
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Display working hours for each location */}
      {locations.length > 0 && (
        <div className="flex flex-column gap-2">
          <div>
            <p className="mb-0 fs-4">
              <FormattedMessage id="ai.settings.workingHours" />
            </p>
            <p className="mb-0 description fs-3">
              <FormattedMessage id="ai.settings.workingHours.desc" />
            </p>
          </div>
          <div className="visito-card">
            <div className="visito-card-body">
              {locations.map(({ name, id }: { name: string; id: string }) => {
                const aiLocation = stateLocations?.find(
                  (l: any) => l.location === id,
                );
                const workingHours = aiLocation?.workingHours || {
                  active: false,
                  from: "09:00",
                  to: "18:00",
                };
                return (
                  <div className="flex gap-4 justify-content-between" key={id}>
                    <div className="flex align-items-center gap-2">
                      <IonToggle
                        checked={workingHours.active}
                        onIonChange={(ev) =>
                          handleWorkingHoursChange(id, "workinghours", {
                            ...workingHours,
                            active: ev.detail.checked,
                          })
                        }
                        mode="ios"
                        className="ios visito-toggle"
                      />
                      {name}
                    </div>
                    <div className="flex gap-4">
                      <div>
                        <IonSelect
                          label="From"
                          placeholder="Select One"
                          value={workingHours.from}
                          interface="popover"
                          onIonChange={(ev) =>
                            handleWorkingHoursChange(id, "workinghours", {
                              ...workingHours,
                              from: ev.detail.value,
                            })
                          }
                        >
                          {[...Array(24).keys()].map((i) => (
                            <IonSelectOption
                              key={i}
                              value={i < 10 ? `0${i}:00` : `${i}:00`}
                            >
                              {i < 10 ? `0${i}:00` : `${i}:00`}
                            </IonSelectOption>
                          ))}
                        </IonSelect>
                      </div>
                      <div>
                        <IonSelect
                          label="To"
                          placeholder="Select One"
                          value={workingHours.to}
                          interface="popover"
                          onIonChange={(ev) =>
                            handleWorkingHoursChange(id, "workinghours", {
                              ...workingHours,
                              to: ev.detail.value,
                            })
                          }
                        >
                          {[...Array(24).keys()].map((i) => (
                            <IonSelectOption
                              key={i}
                              value={i < 10 ? `0${i}:00` : `${i}:00`}
                            >
                              {i < 10 ? `0${i}:00` : `${i}:00`}
                            </IonSelectOption>
                          ))}
                        </IonSelect>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Modal for creating/updating help rules */}
      <IonModal
        id="new-conversation-modal"
        isOpen={isModalOpen}
        onDidDismiss={() => onHideModal()}
      >
        {isMobile && (
          <IonHeader>
            <IonToolbar style={{ paddingLeft: 0, paddingRight: 0 }}>
              <IonTitle>
                <FormattedMessage id="newConv.Title" />
              </IonTitle>
            </IonToolbar>
          </IonHeader>
        )}
        <Wrapper>
          <form
            id="new-conversation"
            className={isMobile ? "" : "inner-content"}
            onSubmit={onBeforeSubmit}
          >
            {!isMobile && (
              <div className={modalStyles.header}>
                <h2 className="mb-0 fs-4">
                  <FormattedMessage id="ai.settings.escalations.rules.new" />
                </h2>
              </div>
            )}

            <div
              className={modalStyles.body}
              style={{ padding: isMobile ? "1rem 1.25em" : "1rem" }}
            >
              {isMobile && (
                <p className="mb-0 fs-3 description">
                  <FormattedMessage id="newConv.Desc" />
                </p>
              )}
              <div className="flex flex-column gap-2">
                <label htmlFor="name" className="fs-3">
                  <FormattedMessage id="ai.settings.escalations.rules.name" />
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  className={inputStyles.input}
                  value={currentRule?.name || ""}
                  onChange={(e) =>
                    setCurrentRule((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  autoComplete="off"
                  required
                  disabled={mutateUpdateHelpRule.isPending}
                />
              </div>

              <div className="flex flex-column gap-2">
                <label
                  htmlFor="description"
                  className="fs-3 flex flex-column gap-1"
                >
                  <FormattedMessage id="ai.settings.escalations.rules.description" />
                  <span className="description fs-2">
                    <FormattedMessage id="ai.settings.escalations.rules.details" />
                  </span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={7}
                  value={currentRule?.description || ""}
                  onChange={(e) =>
                    setCurrentRule((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className={inputStyles.input}
                  disabled={mutateUpdateHelpRule.isPending}
                  required
                />
              </div>
            </div>

            {!isMobile && (
              <div className={modalStyles.footer}>
                <button
                  className="btn btn-visito-light w-100"
                  onClick={onBeforeDismiss}
                  disabled={mutateUpdateHelpRule.isPending}
                >
                  <FormattedMessage id="common.cancel" />
                </button>
                <button
                  className="btn btn-visito-primary w-100"
                  type="submit"
                  style={{
                    pointerEvents: mutateUpdateHelpRule.isPending
                      ? "none"
                      : "auto",
                  }}
                >
                  {mutateUpdateHelpRule.isPending ? (
                    "..."
                  ) : (
                    <FormattedMessage id="ai.settings.escalations.rules.create" />
                  )}
                </button>
              </div>
            )}
          </form>
        </Wrapper>
        {isMobile && (
          <IonFooter>
            <IonToolbar style={{ paddingLeft: 0, paddingRight: 0 }}>
              <div
                className={modalStyles.footer}
                style={{ borderTop: "0px", padding: "0.5rem 1em" }}
              >
                <button
                  className="btn btn-visito-light w-100"
                  onClick={onHideModal}
                  disabled={mutateUpdateHelpRule.isPending}
                >
                  <FormattedMessage id="common.cancel" />
                </button>
                <button
                  className="btn btn-visito-primary w-100"
                  type="submit"
                  form="new-conversation"
                  style={{
                    pointerEvents: mutateUpdateHelpRule.isPending
                      ? "none"
                      : "auto",
                  }}
                >
                  {mutateUpdateHelpRule.isPending ? (
                    "..."
                  ) : (
                    <FormattedMessage id="ai.settings.escalations.rules.create" />
                  )}
                </button>
              </div>
            </IonToolbar>
          </IonFooter>
        )}
      </IonModal>

      {/* Delete alert */}
      <IonAlert
        isOpen={openAlert}
        header={intl.formatMessage({
          id: "ai.settings.escalations.rules.delete.title",
        })}
        message={intl.formatMessage({
          id: "ai.settings.escalations.rules.delete.desc",
        })}
        buttons={[
          {
            text: intl.formatMessage({ id: "common.cancel" }),
            role: "cancel",
            handler: onHideAlert,
            cssClass: `${globalOutboundStyles.alertCancel} ${globalOutboundStyles.alertBtn}`,
          },
          {
            text: intl.formatMessage({
              id: "common.confirmDelete",
            }),
            role: "confirm",
            handler: onDeleteRule,
            cssClass: `${globalOutboundStyles.alertConfirm} ${globalOutboundStyles.alertBtn}`,
          },
        ]}
        onDidDismiss={onHideAlert}
      />
    </div>
  );
}
